import { ChallengeSituation } from "models/challenges/Challenge";

import { getChallengeAPI, getChallengeForInvestorChallengeConfigAPI } from "api/ChallengeAPI";
import { getChallengeTendersByChallengeAPI } from "api/ChallengeTenderAPI/index";
import { getConfigForChallengeAPI } from "api/ChallengeAPI/index";
import { getInvitationsForChallengeAPI } from "api/ChallengeAPI/index";
import { getUniversity } from "api/BusinessProfileAPI";

import BusinessProfileModel from "models/business-profiles/BusinessProfile";
import CategorySelectableModel from "models/categories/CategorySelectable";
import ChallengeModel from "../../models/challenges/Challenge";
import ChallengeTenderModel from "models/challenge-tenders/ChallengeTender";
import { MicroBusinessProfileTypes } from "constants/Profiles";
import ProfileModel from "models/profiles/Profile";

class ChallengeService {
	private readonly accessToken: string;

	constructor(accessToken: string) {
		this.accessToken = accessToken;
	}

	async getOne(challengeSlug: string): Promise<ChallengeModel> {
		const challengeResponseAPI = await getChallengeAPI(challengeSlug, this.accessToken)();
		let challengeModel = ChallengeModel.generateEmpty();

		if (!challengeResponseAPI.message) {
			challengeModel = ChallengeModel.generateFromAPI(challengeResponseAPI);
		}

		return challengeModel;
	}

	async getChallengeProfile(challengeProfileSlug: string): Promise<BusinessProfileModel> {
		let profile: BusinessProfileModel;

		try {
			const profileFromAPI = await getUniversity(challengeProfileSlug, this.accessToken)();
			profile = BusinessProfileModel.generateFromAPI(profileFromAPI);
		} catch (err) {
			profile = BusinessProfileModel.generateEmpty();
		}

		return profile;
	}

	async getChallengeTenders(idChallenge: number) {
		const challengeTenders: ChallengeTenderModel[] = [];
		const challengeTendersResponseAPI = await getChallengeTendersByChallengeAPI(idChallenge, this.accessToken)();

		if (challengeTendersResponseAPI.length > 0) {
			const listTenders: ChallengeTenderModel[] = challengeTendersResponseAPI.map((tenderAPI: any) => {
				const tenderModel = ChallengeTenderModel.generateFromAPI(tenderAPI);
				const profileWithJustSlug = ProfileModel.generateEmpty();
				profileWithJustSlug.links.link = tenderAPI.admin.slug;
				profileWithJustSlug.info.name = tenderAPI.admin.name;

				tenderModel.admin = profileWithJustSlug;

				return tenderModel;
			});

			challengeTenders.push(...listTenders);
		}

		return challengeTenders;
	}

	async getChallengeGuestSituation(challengeSlug: string): Promise<ChallengeSituation | null> {
		let guestSituation: ChallengeSituation | null;

		try {
			const invitationsForChallenge = await getInvitationsForChallengeAPI(challengeSlug, this.accessToken)();

			if (invitationsForChallenge && invitationsForChallenge[0] && invitationsForChallenge[0].destination) {
				guestSituation = invitationsForChallenge[0].situation as ChallengeSituation;
			} else {
				guestSituation = null;
			}
		} catch (err) {
			guestSituation = null;
		}

		return guestSituation;
	}

	async getChallengeGuest(challengeSlug: string): Promise<BusinessProfileModel | null> {
		let guestProfile: BusinessProfileModel | null;

		try {
			const invitationsForChallenge = await getInvitationsForChallengeAPI(challengeSlug, this.accessToken)();

			if (invitationsForChallenge && invitationsForChallenge[0] && invitationsForChallenge[0].destination) {
				guestProfile = await this.getChallengeProfile(invitationsForChallenge[0].destination.slug);
			} else {
				guestProfile = null;
			}
		} catch (err) {
			guestProfile = null;
		}

		return guestProfile;
	}

	async getConfigForChallenge(
		profileID: string,
		businessProfileType: MicroBusinessProfileTypes,
	): Promise<{
		companyEmail: string;
		// We'll use this email as the generic one
		institutionEmail: string;
		schools: any[];
		sectors: any[];
		firstTimeOnTheSection: boolean;
	}> {
		if (
			businessProfileType === MicroBusinessProfileTypes.ACCELERATOR ||
			businessProfileType === MicroBusinessProfileTypes.INCUBATOR
		) {
			return getChallengeForInvestorChallengeConfigAPI(profileID, this.accessToken)().then((res) => {
				switch (businessProfileType) {
					case MicroBusinessProfileTypes.INCUBATOR:
						const currentIncubatorEmail = res.incubator_email;
						const currentIncubatorSectors = res.incubator_sectors
							? res.incubator_sectors.map((sector: any) => CategorySelectableModel.generateFromAPI(sector))
							: [];

						return {
							companyEmail: currentIncubatorEmail,
							institutionEmail: currentIncubatorEmail,
							schools: [],
							sectors: currentIncubatorSectors,
							firstTimeOnTheSection: Boolean(res.message || !currentIncubatorEmail),
						};
					case MicroBusinessProfileTypes.ACCELERATOR:
						const currentAcceleratorEmail = res.accelerator_email;
						const currentAcceleratorSectors = res.accelerator_sectors
							? res.accelerator_sectors.map((sector: any) => CategorySelectableModel.generateFromAPI(sector))
							: [];

						return {
							companyEmail: currentAcceleratorEmail,
							institutionEmail: currentAcceleratorEmail,
							schools: [],
							sectors: currentAcceleratorSectors,
							firstTimeOnTheSection: Boolean(res.message || !currentAcceleratorEmail),
						};
				}
			});
		} else {
			return getConfigForChallengeAPI(profileID, this.accessToken)().then((res) => {
				switch (businessProfileType) {
					case MicroBusinessProfileTypes.UNIVERSITY:
						const currentCompanyEmail = res.company_email;
						const currentUniversityEmail = res.institutions_email;
						const currentSchools = res.institution_challenge_schools
							? res.institution_challenge_schools.map((school: any) => CategorySelectableModel.generateFromAPI(school))
							: [];
						const currentUniversitySectors = res.company_challenge_sectors
							? res.company_challenge_sectors.map((sector: any) => CategorySelectableModel.generateFromAPI(sector))
							: [];

						return {
							companyEmail: currentCompanyEmail,
							institutionEmail: currentUniversityEmail,
							schools: currentSchools,
							sectors: currentUniversitySectors,
							firstTimeOnTheSection: Boolean(res.message || (!currentCompanyEmail && !currentUniversityEmail)),
						};
					case MicroBusinessProfileTypes.INSTITUTION:
						const currentInstitutionEmail = res.institutions_email;
						const currentInstitutionSectors = res.institution_challenge_sectors
							? res.institution_challenge_sectors.map((sector: any) => CategorySelectableModel.generateFromAPI(sector))
							: [];

						return {
							companyEmail: currentInstitutionEmail,
							institutionEmail: currentInstitutionEmail,
							schools: [],
							sectors: currentInstitutionSectors,
							firstTimeOnTheSection: Boolean(res.message || !currentInstitutionEmail),
						};
					case MicroBusinessProfileTypes.COMPANY:
						return {
							companyEmail: "",
							institutionEmail: "",
							schools: [],
							sectors: [],
							firstTimeOnTheSection: false,
						};
					default:
						return {
							companyEmail: "",
							institutionEmail: "",
							schools: [],
							sectors: [],
							firstTimeOnTheSection: false,
						};
				}
			});
		}
	}
}

export default ChallengeService;
